import { FC, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-daisyui';
import { EllipsisHorizontalIcon, TrashIcon } from '@heroicons/react/24/outline';
import DeleteTripModal from '@/components/modals/DeleteTripModal/DeleteTripModal.tsx';
import { cn } from '@/utils/classNames.ts';

type TripActionsMenuProps = {
  tripId: string;
  disabled?: boolean;
  className?: string;
};

const TripActionsMenu: FC<TripActionsMenuProps> = ({ tripId, disabled = false, className }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isMenuOpen) return;

    const frame = requestAnimationFrame(() => firstItemRef.current?.focus());

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setIsMenuOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <Button
        ref={triggerRef}
        color="primary"
        shape="circle"
        disabled={disabled}
        aria-label={t('tripPage.trip_options')}
        title={t('tripPage.trip_options')}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        aria-controls={menuId}
        onClick={() => setIsMenuOpen((open) => !open)}
        className="h-[38px] min-h-[38px] w-[38px] p-2"
      >
        <EllipsisHorizontalIcon aria-hidden="true" />
      </Button>

      <div
        id={menuId}
        role="menu"
        aria-label={t('tripPage.trip_options')}
        className={cn(
          'absolute right-0 top-[calc(100%+0.5rem)] min-w-48 origin-top-right rounded-2xl border border-primary/30 bg-journi-theme-light p-1.5',
          'shadow-[0_16px_40px_-16px_rgba(10,10,10,0.45),0_2px_6px_rgba(10,10,10,0.08)]',
          'transition-[opacity,transform,visibility] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-[opacity,visibility]',
          isMenuOpen
            ? 'visible scale-100 opacity-100 duration-150'
            : 'invisible scale-95 opacity-0 duration-100 motion-reduce:scale-100'
        )}
      >
        <button
          ref={firstItemRef}
          type="button"
          role="menuitem"
          onClick={() => {
            setIsMenuOpen(false);
            setIsDeleteOpen(true);
          }}
          className="flex min-h-10 w-full items-center gap-2.5 rounded-xl px-3 text-left font-sans text-sm font-medium text-[#2F2D2A] outline-none transition-colors duration-150 focus-visible:bg-error/10 focus-visible:text-error [@media(hover:hover)]:hover:bg-error/10 [@media(hover:hover)]:hover:text-error"
        >
          <TrashIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('button.delete_trip')}
        </button>
      </div>

      {isDeleteOpen ? <DeleteTripModal tripId={tripId} isOpen onClose={() => setIsDeleteOpen(false)} /> : null}
    </div>
  );
};

export default TripActionsMenu;
